import { useState, useEffect, useCallback } from 'react'
import {
  MdArrowBack,
  MdEdit,
  MdFlight,
  MdSchedule,
  MdLocationOn,
  MdPerson,
  MdAirplaneTicket,
  MdTrendingUp,
  MdTrendingDown,
  MdAccessTime,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdRefresh,
  MdLocalGasStation,
  MdGroup,
  MdPhone,
  MdCalendarToday,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner } from '../../components/ui'
import { useFlight } from '../../hooks/useFlight'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import {
  formatTime,
  formatDate,
  formatDuration,
  getStatusColor,
  getSeatAvailabilityColor,
  formatPrice,
} from '../../utils/flight'
import { capitalizeStatus } from '../../utils/string'
import { getWebsocketChannel } from '../../utils/websocket'

function FlightDetailPage() {
  const dispatch = useDispatch()
  const { flightId } = useParams()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const navigate = useNavigate()
  const [flight, setFlight] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const { getFlightAsync } = useFlight()

  const loadFlight = useCallback(async () => {
    if (!flightId) return

    try {
      setIsLoading(true)
      const flightData = await getFlightAsync(flightId)
      setFlight(flightData)
    } catch (error) {
      console.error('Error loading flight:', error)
    } finally {
      setIsLoading(false)
    }
  }, [flightId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (flightId) {
      loadFlight()
    }
  }, [flightId, loadFlight])

  useEffect(() => {
    const channelName = getWebsocketChannel(flightId).flightDetails

    if (flightId && channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (flightId && channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return <MdSchedule className="text-primary-600 dark:text-primary-400" />
      case 'in flight':
      case 'inflight':
        return <MdFlight className="text-success-600 dark:text-success-400" />
      case 'delayed':
        return <MdWarning className="text-warning-600 dark:text-warning-400" />
      case 'cancelled':
        return <MdError className="text-red-600 dark:text-red-400" />
      case 'completed':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      default:
        return <MdAccessTime className="text-neutral-500 dark:text-neutral-400" />
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!flight) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Flight not found
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The flight you're looking for doesn't exist or has been removed.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2"
          >
            <MdArrowBack className="text-lg" />
            <span>Back to Flights</span>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 mb-4"
          >
            <MdArrowBack />
            <span>Back to Flights</span>
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-neutral-900 dark:text-white mb-2 text-left">
              Flight {flight.number}
            </h1>
            <p className="text-base lg:text-lg text-neutral-600 dark:text-neutral-400 text-left">
              {flight.route} • {flight.airline?.name} • {formatDate(flight.departure)}
            </p>
          </div>

          <div className="flex items-center space-x-3 flex-shrink-0">
            <Button variant="ghost" onClick={loadFlight} disabled={isLoading}>
              <MdRefresh className={isLoading ? 'animate-spin' : ''} />
            </Button>

            <div className="flex items-center space-x-2">
              {getStatusIcon(flight.status)}
              <span
                className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-full ${getStatusColor(flight.status)}`}
              >
                {capitalizeStatus(flight.status)}
              </span>
            </div>

            <Button
              variant="primary"
              onClick={() => {
                /* TODO: Implement edit flight */
              }}
              className="flex items-center space-x-2"
            >
              <MdEdit className="text-lg" />
              <span>Edit Flight</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Flight Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Flight Route */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Flight Route
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Origin */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <MdTrendingUp className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <h4 className="font-medium text-neutral-900 dark:text-white">Departure</h4>
                </div>
                <div className="ml-10 space-y-2">
                  <p className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {flight.origin?.airport?.name || 'N/A'}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {flight.origin?.airport?.code} • {flight.origin?.airport?.city},{' '}
                    {flight.origin?.airport?.stateCode}
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <MdLocationOn className="text-xs" />
                    <span>Terminal {flight.origin?.terminal || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <MdLocationOn className="text-xs" />
                    <span>Gate {flight.origin?.gate || 'N/A'}</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Scheduled: {formatTime(flight.origin?.scheduledTime)}
                    </p>
                    {flight.origin?.actualTime && (
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        Actual: {formatTime(flight.origin?.actualTime)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Destination */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                    <MdTrendingDown className="text-green-600 dark:text-green-400" />
                  </div>
                  <h4 className="font-medium text-neutral-900 dark:text-white">Arrival</h4>
                </div>
                <div className="ml-10 space-y-2">
                  <p className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {flight.destination?.airport?.name || 'N/A'}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {flight.destination?.airport?.code} • {flight.destination?.airport?.city},{' '}
                    {flight.destination?.airport?.stateCode}
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <MdLocationOn className="text-xs" />
                    <span>Terminal {flight.destination?.terminal || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <MdLocationOn className="text-xs" />
                    <span>Gate {flight.destination?.gate || 'N/A'}</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Scheduled: {formatTime(flight.destination?.scheduledTime)}
                    </p>
                    {flight.destination?.actualTime && (
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        Actual: {formatTime(flight.destination?.actualTime)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flight Details */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Flight Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <MdFlight className="text-neutral-500 dark:text-neutral-400" />
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Duration</p>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {formatDuration(flight.duration)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MdAirplaneTicket className="text-neutral-500 dark:text-neutral-400" />
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Price</p>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {formatPrice(flight.price)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MdGroup className="text-neutral-500 dark:text-neutral-400" />
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Seats</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{flight.seats}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MdCheckCircle className="text-neutral-500 dark:text-neutral-400" />
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Available</p>
                  <p
                    className={`font-medium ${getSeatAvailabilityColor(flight.seatsAvailable, flight.seats)}`}
                  >
                    {flight.seatsAvailable}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MdPerson className="text-neutral-500 dark:text-neutral-400" />
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Booked</p>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {flight.seatsBooked}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MdLocalGasStation className="text-neutral-500 dark:text-neutral-400" />
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Fuel Burn</p>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {flight.actualFuelBurn || flight.estimatedFuelBurn || 'N/A'} L
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Delay Information */}
          {flight.delayReason && (
            <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                Delay Information
              </h3>
              <div className="flex items-start space-x-3">
                <MdWarning className="text-warning-600 dark:text-warning-400 mt-1" />
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white mb-1">Delay Reason</p>
                  <p className="text-neutral-600 dark:text-neutral-400">{flight.delayReason}</p>
                </div>
              </div>
            </div>
          )}

          {/* Baggage Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Baggage Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-white mb-3">Allowance</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">Carry-on</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flight.baggageAllowance?.carryOn || 0} piece(s)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">Checked</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flight.baggageAllowance?.checked || 0} piece(s)
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-white mb-3">Status</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">Loaded</span>
                    <span
                      className={`font-medium ${flight.baggageStatus?.loaded ? 'text-success-600 dark:text-success-400' : 'text-neutral-500 dark:text-neutral-400'}`}
                    >
                      {flight.baggageStatus?.loaded ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">Unloaded</span>
                    <span
                      className={`font-medium ${flight.baggageStatus?.unloaded ? 'text-success-600 dark:text-success-400' : 'text-neutral-500 dark:text-neutral-400'}`}
                    >
                      {flight.baggageStatus?.unloaded ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Flight Status */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Flight Status
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Status</span>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(flight.status)}`}
                >
                  {capitalizeStatus(flight.status)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Type</span>
                <span className="font-medium text-neutral-900 dark:text-white capitalize">
                  {flight.flightType}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Operational</span>
                <span className="font-medium text-neutral-900 dark:text-white capitalize">
                  {flight.operationalStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Airline Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Airline</h3>
            <div className="space-y-3">
              <div>
                <p className="font-medium text-neutral-900 dark:text-white">
                  {flight.airline?.name}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {flight.airline?.code}
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <MdLocationOn className="text-xs" />
                  <span>{flight.airline?.country}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <MdPhone className="text-xs" />
                  <span>{flight.airline?.phone}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <MdCalendarToday className="text-xs" />
                  <span>Founded: {formatDate(flight.airline?.founded)}</span>
                </div>
              </div>
              {flight.airline?.headquarters && (
                <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white mb-1">
                    Headquarters
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {flight.airline.headquarters.city}, {flight.airline.headquarters.country}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Aircraft Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Aircraft
            </h3>
            <div className="space-y-3">
              <div>
                <p className="font-medium text-neutral-900 dark:text-white">
                  {flight.aircraft?.name}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {flight.aircraft?.code}
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Model</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flight.aircraft?.maker} {flight.aircraft?.model}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Type</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flight.aircraft?.type}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Year</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flight.aircraft?.year}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Serial</span>
                  <span className="font-medium text-neutral-900 dark:text-white text-xs">
                    {flight.aircraft?.serialNumber}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Crew Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Flight Crew
            </h3>
            <div className="space-y-3">
              <div
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors"
                onClick={() => navigate(`/pilots/${flight.crew?.captain?._id}`)}
              >
                <MdPerson className="text-neutral-500 dark:text-neutral-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    Captain {flight.crew?.captain?.firstName} {flight.crew?.captain?.lastName}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    {flight.crew?.captain?.number}
                  </p>
                </div>
              </div>
              <div
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors"
                onClick={() => navigate(`/pilots/${flight.crew?.firstOfficer?._id}`)}
              >
                <MdPerson className="text-neutral-500 dark:text-neutral-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    First Officer {flight.crew?.firstOfficer?.firstName}{' '}
                    {flight.crew?.firstOfficer?.lastName}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    {flight.crew?.firstOfficer?.number}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-3 p-2">
                  <MdGroup className="text-neutral-500 dark:text-neutral-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                      Flight Attendants
                    </p>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">
                      {flight.crew?.flightAttendants?.length || 0} assigned
                    </p>
                  </div>
                </div>
                {flight.crew?.flightAttendants?.map((attendant, index) => (
                  <div
                    key={attendant._id}
                    className="ml-8 p-2 rounded bg-neutral-50 dark:bg-neutral-800"
                  >
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                      {attendant.firstName} {attendant.lastName}
                    </p>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">
                      {attendant.employeeNumber}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FlightDetailPage
